import type { Aporte } from '../../lib/sesion';
import type { PropsVista } from './registro';

/**
 * Vista `mapa`: plano cartesiano 2D de la Pantalla (legible a 6 m) que ubica
 * cada actor por interés × influencia. Solo contenido anónimo (C7): el punto
 * es el actor nombrado por la sala, jamás quién lo aportó. Respeta `opciones`
 * del manifiesto: `titulo`, `eje_x`, `eje_x_etiqueta`, `eje_y`,
 * `eje_y_etiqueta`, `min`, `max`, `etiqueta_punto` y `por_grupo` (si es true,
 * se muestra cuántas mesas aportaron — agregado, nunca por persona).
 */

/** Un actor ya consolidado: posición promedio y cuántas veces lo ubicaron. */
interface PuntoMapa {
  /** Nombre tal como lo escribió la sala la primera vez (para mostrar). */
  etiqueta: string;
  /** Posición promedio en unidades del manifiesto (min..max). */
  x: number;
  y: number;
  /** Cuántos aportes ubicaron a este mismo actor (badge ×N). */
  veces: number;
}

/** Clave de consolidación: minúsculas, sin tildes, espacios colapsados. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Lee un número finito del payload en la clave declarada, o null. */
function numeroDe(aporte: Aporte, clave: string): number | null {
  const v = (aporte.payload ?? {})[clave];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function VistaMapa({ aportes, porGrupo, opciones }: PropsVista) {
  const titulo = typeof opciones.titulo === 'string' ? opciones.titulo : 'Así quedó el mapa';
  const ejeX = typeof opciones.eje_x === 'string' ? opciones.eje_x : 'interes';
  const ejeY = typeof opciones.eje_y === 'string' ? opciones.eje_y : 'influencia';
  const ejeXEtiqueta =
    typeof opciones.eje_x_etiqueta === 'string' ? opciones.eje_x_etiqueta : 'Interés';
  const ejeYEtiqueta =
    typeof opciones.eje_y_etiqueta === 'string' ? opciones.eje_y_etiqueta : 'Influencia';
  const min = typeof opciones.min === 'number' ? opciones.min : 1;
  const max = typeof opciones.max === 'number' && opciones.max > min ? opciones.max : min + 4;
  const etiquetaPunto =
    typeof opciones.etiqueta_punto === 'string' ? opciones.etiqueta_punto : 'actor';
  const mostrarGrupos = opciones.por_grupo === true;

  // Consolidación: el MISMO actor (texto normalizado) aparece una sola vez,
  // en el promedio de todas las posiciones donde la sala lo ubicó.
  const porActor = new Map<string, { etiqueta: string; sumaX: number; sumaY: number; n: number }>();
  for (const a of aportes) {
    const crudo = (a.payload ?? {})[etiquetaPunto];
    if (typeof crudo !== 'string' || crudo.trim().length === 0) continue;
    const x = numeroDe(a, ejeX);
    const y = numeroDe(a, ejeY);
    if (x == null || y == null) continue; // sin ambos ejes numéricos no hay punto
    const clave = normalizar(crudo);
    const previo = porActor.get(clave);
    if (previo) {
      previo.sumaX += x;
      previo.sumaY += y;
      previo.n += 1;
    } else {
      porActor.set(clave, { etiqueta: crudo.trim(), sumaX: x, sumaY: y, n: 1 });
    }
  }
  if (porActor.size === 0) return null;

  const rango = max - min;
  const puntos: PuntoMapa[] = [...porActor.values()].map((p) => ({
    etiqueta: p.etiqueta,
    x: p.sumaX / p.n,
    y: p.sumaY / p.n,
    veces: p.n,
  }));

  // % dentro del plano, con clamp por si un payload viejo trae valores fuera.
  const pct = (v: number) => Math.min(100, Math.max(0, ((v - min) / rango) * 100));
  const mesas = porGrupo.filter((g) => g.n > 0).length;

  return (
    <section className="mt-10 w-full max-w-4xl">
      <h2 className="mb-6 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
        {titulo}
      </h2>

      <div className="flex items-stretch justify-center gap-3">
        {/* Etiqueta del eje Y, vertical, pegada al plano */}
        <div className="flex items-center">
          <span
            className="font-sans text-lg uppercase tracking-wide text-content-muted"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {ejeYEtiqueta}
          </span>
        </div>

        {/* Plano cartesiano: cuadrado, alto máximo ~60vh */}
        <div className="w-full" style={{ maxWidth: '60vh' }}>
          <div className="relative aspect-square w-full rounded-lg border border-border-strong bg-surface-raised">
            {/* Líneas medias: parten el plano en 4 cuadrantes */}
            <div className="absolute inset-y-0 left-1/2 border-l border-border" aria-hidden />
            <div className="absolute inset-x-0 top-1/2 border-t border-border" aria-hidden />

            {/* Extremos de escala en las esquinas (referencia de lectura) */}
            <span className="absolute bottom-1 left-2 font-sans text-base tabular-nums text-content-muted">
              {min}
            </span>
            <span className="absolute bottom-1 right-2 font-sans text-base tabular-nums text-content-muted">
              {max}
            </span>
            <span className="absolute left-2 top-1 font-sans text-base tabular-nums text-content-muted">
              {max}
            </span>

            {/* Un punto por actor consolidado, SIN autores (C7) */}
            {puntos.map((p) => {
              const izquierda = pct(p.x);
              const abajo = pct(p.y);
              // En la mitad derecha la etiqueta va a la izquierda del punto
              // para no desbordar el plano.
              const haciaIzquierda = izquierda > 55;
              return (
                <div
                  key={normalizar(p.etiqueta)}
                  className={`absolute flex items-center gap-2 ${haciaIzquierda ? 'flex-row-reverse' : ''}`}
                  style={{
                    left: `${izquierda}%`,
                    bottom: `${abajo}%`,
                    transform: haciaIzquierda
                      ? 'translate(calc(-100% + 0.625rem), 50%)'
                      : 'translate(-0.625rem, 50%)',
                  }}
                >
                  <span className="h-5 w-5 shrink-0 rounded-full bg-cyan" aria-hidden />
                  <span className="max-w-56 truncate font-display text-xl font-semibold leading-tight text-content">
                    {p.etiqueta}
                  </span>
                  {p.veces > 1 && (
                    <span className="shrink-0 rounded-full bg-surface-sunken px-2 font-sans text-base font-semibold tabular-nums text-content-muted">
                      ×{p.veces}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Etiqueta del eje X, centrada bajo el plano */}
          <p className="mt-2 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
            {ejeXEtiqueta}
          </p>
        </div>
      </div>

      {mostrarGrupos && mesas > 0 && (
        <p className="mt-4 text-center font-sans text-lg text-content-muted">
          Aportaron <span className="font-semibold tabular-nums text-content">{mesas}</span>{' '}
          {mesas === 1 ? 'mesa de trabajo' : 'mesas de trabajo'}
        </p>
      )}
    </section>
  );
}
